import { IRecipe, IRecipeBook } from "../../types/recipe";
import { getApi, postApi, putApi } from "../api";
import { RecipeDb } from "../indexeddb/recipeDb";
import { getStringFromLocalStorage } from "../localStorage";

const apiUrl = import.meta.env.VITE_API_URL as string;
const apiToken = getStringFromLocalStorage("idToken");

export class RecipeStore {
  private static instance: RecipeStore | null = null;
  private _readyPromise: Promise<void>;
  private db = new RecipeDb();
  public householdRecipes: IRecipe[] = [];
  public emptyRecipes: IRecipe[] = [];
  private constructor() {
    this._readyPromise = this.load();
  }

  public static getInstance(): RecipeStore {
    if (!RecipeStore.instance) {
      RecipeStore.instance = new RecipeStore();
    }
    return RecipeStore.instance;
  }

  public ready(): Promise<void> {
    return this._readyPromise;
  }

  private async load(): Promise<void> {
    await this.db.initDB();
    await this.getHouseholdRecipes();
  }

  public async getHouseholdRecipes(clear: boolean = false): Promise<void> {
    try {
      const local = await this.db.getRecipes();
      console.log(`getHouseholdRecipes called with clear=${clear}, local recipes count: ${local.length}`);

      if (local.length === 0 || clear) {
        console.log('Fetching recipes from API...');
        const api = await getApi(apiUrl, "recipe", apiToken) as IRecipe[];
        console.log(`API returned ${api.length} recipes`);

        const sorted = api.sort((a, b) => a.name.localeCompare(b.name));
        await this.db.addRecipes(sorted, clear);
        console.log(`Stored ${sorted.length} recipes in IndexedDB`);

        this.householdRecipes = [];
        this.householdRecipes = sorted;
      } else {
        console.log('Using cached recipes from IndexedDB');
        this.householdRecipes = local;
      }

      console.log(`Final householdRecipes count: ${this.householdRecipes.length}`);
    } catch (e: any) {
      console.error('Error in getHouseholdRecipes:', e);
    }
  }

  public async getEmptyRecipes(): Promise<void> {
    try {
      console.log("RecipeStore: Fetching empty recipes from API...");
      const api = await getApi(apiUrl, "recipe", apiToken, { empty: "true" });
      console.log("RecipeStore: API response for empty recipes:", api);
      console.log("RecipeStore: API response type:", typeof api);
      console.log("RecipeStore: Is array?", Array.isArray(api));

      if (Array.isArray(api)) {
        this.emptyRecipes = api;
      } else {
        console.warn("RecipeStore: API did not return an array, initializing as empty array");
        this.emptyRecipes = [];
      }

      console.log("RecipeStore: emptyRecipes set to:", this.emptyRecipes);
      console.log("RecipeStore: emptyRecipes count:", this.emptyRecipes.length);
    } catch (e: any) {
      console.error("RecipeStore: Error fetching empty recipes:", e);
      this.emptyRecipes = [];
    }
  }

  public async addRecipe(recipeName: string, defaultPeople: number): Promise<void> {
    try {
      await postApi(apiUrl, "recipe", { name: recipeName, default_people: defaultPeople }, apiToken);
      this.getHouseholdRecipes(true);
    } catch (e: any) {
      console.log(e);
    }
  }

  public async setRecipeBook(recipe: IRecipe, recipe_book: IRecipeBook | undefined): Promise<void> {
    try {
      if (recipe_book) {
        await putApi(apiUrl, "recipe", { ...recipe, recipe_book_id: recipe_book.id }, apiToken);
        this.db.updateRecipe({ ...recipe, recipe_book });
      } else {
        await putApi(apiUrl, "recipe", { ...recipe, recipe_book_id: null }, apiToken);
        this.db.updateRecipe({ ...recipe, recipe_book: undefined });
      }

    } catch (e: any) {
      console.log(e);
    }
  }

  public async setRecipeBookPage(recipe: IRecipe, recipe_book_page: number | undefined): Promise<void> {
    try {
      if (recipe_book_page) {
        await putApi(apiUrl, "recipe", { ...recipe, recipe_book_page: recipe_book_page }, apiToken);
        this.db.updateRecipe({ ...recipe, recipe_book_page });
      } else {
        await putApi(apiUrl, "recipe", { ...recipe, recipe_book_page: null }, apiToken);
        this.db.updateRecipe({ ...recipe, recipe_book_page: undefined });
      }
    } catch (e: any) {
      console.log(e);
    }
  }
}
